import {
  ref,
  computed,
  watch,
  onUnmounted,
  onMounted,
  onDeactivated,
  onActivated,
  inject,
  nextTick,
} from "vue";
import { useStore } from "vuex";
import { debounce, orderBy } from "lodash";
import { DateTime } from "luxon";
import { ticketEntity } from "../../common/model/entity/ticket-entity";
import { ticketCommentEntity } from "../../common/model/entity/ticket-comment-entity";
import { AddTicketCommentsToCommentEntity } from "../Services/ticket-service";
import { EntitySyncManager } from "../../infrastructure/entity-sync-manager";
import router from "@/router";
import { TicketHelper } from "../../common/helper/ticket-helper";
import { CommonHelper } from "../../common/helper/common-helper";
import { formatPortalSidebarListTimeHover } from "../../common/helper/portal-datetime-format";
import {
  ticketCreatedOnSortMillis,
  ticketMatchesSidebarSearchQuery,
  ticketRecentMessageCreatedOnIso,
  ticketRecentMessageCreatedOnMillis,
  ticketSidebarActivitySortMillis,
} from "../../common/helper/ticket-recent-comment-preview";
import {
  bumpTicketSidebarListRevision,
  ticketSidebarListRevision,
} from "../ticket-sidebar-list-refresh";
import { SIDEBAR_MESSAGE_COMMENT_TYPES } from "../../common/helper/ticket-recent-comment-preview";
import {
  markClientTicketRead,
  ticketActivityMsForClientRead,
} from "../../common/helper/ticket-client-read-state";
import { useQuasar } from "quasar";

const TICKET_LIST_LOAD_MORE_THRESHOLD = 5;
const TICKET_LIST_SCROLL_END_PX = 120;
const TICKET_LIST_SEARCH_DEBOUNCE_MS = 350;

type TicketListSessionCache = {
  hasLoaded: boolean;
  query: string;
  scopeKey: string;
  currentPage: number;
  hasMore: boolean;
};

const ticketListSessionCache: TicketListSessionCache = {
  hasLoaded: false,
  query: "",
  scopeKey: "",
  currentPage: 0,
  hasMore: true,
};

type VirtualScrollExposed = {
  refresh?: (index?: number) => void;
  scrollTo?: (index: number, edge?: string) => void;
};

export function buildLastPublicMessageMillisMap(
  tickets: ticketEntity[],
): Map<string, number> {
  const out = new Map<string, number>();
  const ticketIds = new Set<string>();

  for (const t of tickets) {
    const tid = String(t.ticketId ?? "");
    if (!tid || tid === "0") {
      continue;
    }
    ticketIds.add(tid);
    const ms = ticketRecentMessageCreatedOnMillis(t);
    if (ms > 0) {
      out.set(tid, ms);
    }
  }

  if (ticketIds.size === 0) {
    return out;
  }

  const rows = ticketCommentEntity
    .query()
    .where((c: any) => {
      const tid = String(c.ticketId ?? "");
      if (!ticketIds.has(tid)) {
        return false;
      }
      if (c.isInternal === true || c.isInternal === 1) {
        return false;
      }
      if (c.isDeleted) {
        return false;
      }
      return SIDEBAR_MESSAGE_COMMENT_TYPES.has(String(c.type ?? ""));
    })
    .all();

  for (const c of rows) {
    if (c.isSuccess === false) {
      continue;
    }
    const tid = String(c.ticketId ?? "");
    const dt = DateTime.fromISO(String(c.createdOn ?? ""), { zone: "utc" });
    const ms = dt.isValid ? dt.toMillis() : 0;
    if (ms <= 0) {
      continue;
    }
    const prev = out.get(tid) ?? 0;
    if (ms > prev) {
      out.set(tid, ms);
    }
  }

  return out;
}

function ticketMessagesSidebarSortMillis(
  t: ticketEntity,
  lastPublicMessageByTicket: Map<string, number>,
): number {
  const id = String(t.ticketId ?? "");
  return ticketSidebarActivitySortMillis(
    t,
    lastPublicMessageByTicket.get(id) ?? 0,
  );
}

/**
 * Composable for managing the ticket list logic.
 * Handles ticket filtering, sorting, and selection.
 */
export function useTicketList() {
  const store = useStore();
  const setActiveChatForMobile = inject<((id: string) => void) | undefined>(
    "setActiveChatForMobile",
    undefined,
  );

  const itemsViewportRef = ref<HTMLElement | null>(null);

  /** QVirtualScroll public API (scrollTo / refresh / reset). */

  /**
   * QVirtualScroll measures the viewport before the flex column has a stable height, so
   * `scrollViewSize` can be ~0 and only a handful of rows mount; then layout settles and
   * the slice jumps. `refresh(-1)` recomputes against the real viewport without resetting
   * scroll. Scroll-to-selected runs only on first mount (deep link), not on row clicks.
   */
  const $q = useQuasar();
  let ticketListResizeObserver: ResizeObserver | undefined;
  /** Skips virtual-list sync work after teardown (ResizeObserver / rAF may still fire). */
  let ticketListScopeActive = true;
  // Reactive state
  const searchTicketQuery = ref("");
  const search = searchTicketQuery;
  const virtualListRef = ref<VirtualScrollExposed | null>(null);

  /** Last `Query` sent to `cx/list` (debounced from search). */
  const ticketListServerQuery = ref("");
  const ticketListCurrentPage = ref(0);
  const ticketListHasMore = ref(true);
  const loadingMoreTickets = ref(false);
  let ticketListFetchGeneration = 0;
  let ticketListLoadMoreInFlight = false;
  let ticketListSearchDebounce: ReturnType<typeof setTimeout> | undefined;
  let ticketListScrollEl: HTMLElement | null = null;
  let lastTicketListScopeKey = "";

  /** Drives re-render of relative sidebar times (`formatTicketSidebarTime`) every minute. */
  const sidebarTimeTick = ref(Date.now());
  let sidebarTimeInterval: ReturnType<typeof setInterval> | undefined;

  function startSidebarTimeTick() {
    if (sidebarTimeInterval != null) {
      return;
    }
    sidebarTimeInterval = setInterval(() => {
      sidebarTimeTick.value = Date.now();
    }, 60_000);
  }

  function stopSidebarTimeTick() {
    if (sidebarTimeInterval != null) {
      clearInterval(sidebarTimeInterval);
      sidebarTimeInterval = undefined;
    }
  }

  function ticketSidebarDisplayTimeIso(ticket: ticketEntity): string | null {
    return ticketRecentMessageCreatedOnIso(ticket) ?? ticket.createdOn ?? null;
  }

  function formatTicketSidebarTime(iso: string | null | undefined): string {
    /* Tick only invalidates the template; compare against real now — stale tick
     * made fresh `updatedOn` look "in the future" → CommonHelper showed "Today" until tick advanced. */
    void sidebarTimeTick.value;
    return CommonHelper.formatSidebarListActivityAt(
      String(iso ?? ""),
      Date.now(),
    );
  }

  function formatTicketSidebarTimeHover(
    iso: string | null | undefined,
  ): string {
    void sidebarTimeTick.value;
    return formatPortalSidebarListTimeHover(String(iso ?? ""));
  }

  const isDark = computed(() => $q.dark.isActive);

  // Getters from store
  const getTicketId = computed(
    () => store.getters["ticketDetailModule/getTicketId"] as string,
  );
  const getTicketFillingState = computed(
    () => store.getters["ticketDetailModule/getTicketFillingState"] as boolean,
  );

  const activeChat = getTicketId;

  /**
   * True while initial ticket payload is fetching (FillTickets clears `isTicketFilled` until done).
   * If the ORM already has rows we treat the list as non-blocking so background refills (e.g. the boot-time fill) don't flash this overlay.
   */
  const loadingTicketList = computed(
    () => !getTicketFillingState.value && ticketEntity.query().count() === 0,
  );

  /**
   * Tickets for the Messages sidebar: search filter, then newest-first by latest
   * sidebar comment (`cx/comment/recent` — Messages, Approval, or TPC — and ORM fallback).
   */
  const TicketListForUI = computed(() => {
    void ticketSidebarListRevision.value;
    void getTicketId.value;
    let tickets = ticketEntity.query().all();

    const q = searchTicketQuery.value.trim().toLowerCase();
    // Always filter loaded ORM rows by the current input. Server `Query` handles paging;
    // client filter covers debounce gaps and ensures title/message matching in the sidebar.
    if (q) {
      tickets = tickets.filter((t) => ticketMatchesSidebarSearchQuery(t, q));
    }

    const lastMsgMs = buildLastPublicMessageMillisMap(tickets);

    // Only tickets where a conversation this contact can see has actually happened. `recentComment`
    // is enriched per contact by `cx/list` / `tick/cx/list` and left null when nothing on the
    // ticket is theirs - raised by a technician, opened by an alert rule, or carrying only
    // internal notes - so those rows have no entry here and the sidebar does not list them.
    //
    // Closed tickets inside the reopen window now come from `cx/list` too (Closed is no longer in
    // its ignore list), enriched the same way, so they follow the same rule rather than the blanket
    // exemption they needed while they came unenriched from `closedticket/list`. Not falling back to
    // `recentCommentId`: that is read straight from SQL unscoped, so it is populated for exactly the
    // tickets this filter exists to exclude.
    tickets = tickets.filter(
      (t) => (lastMsgMs.get(String(t.ticketId ?? "")) ?? 0) > 0,
    );

    tickets = orderBy(
      tickets,
      [
        (t) => ticketMessagesSidebarSortMillis(t, lastMsgMs),
        (t) => ticketCreatedOnSortMillis(t),
        (t) => String(t.ticketId ?? ""),
      ],
      ["desc", "desc", "desc"],
    );

    return tickets;
  });

  // Not empty while pages are still coming: the sidebar filter can hide a whole page, and the
  // empty state would flash before the next one lands.
  const isTicketListEmpty = computed(
    () =>
      !loadingTicketList.value &&
      TicketListForUI.value.length === 0 &&
      !loadingMoreTickets.value &&
      !ticketListHasMore.value,
  );

  /**
   * Handles manual ticket selection from the UI.
   * Clears related ticket state, marks as read, and loads comments.
   */
  const markTicketReadForClient = (ticketId: string) => {
    const viewerContactId = store.getters[
      "ContactModule/getContactId"
    ] as number;
    const row = ticketEntity.find(String(ticketId)) as ticketEntity | null;
    if (row) {
      markClientTicketRead(
        viewerContactId,
        ticketId,
        ticketActivityMsForClientRead(row),
      );
    }
    void ticketEntity.update({
      where: (ticket: any) => ticket.ticketId === ticketId,
      data: { isUnread: false },
    });
  };

  const SetSelectedTicketId = async (ticketId: string) => {
    if (ticketId !== getTicketId.value) {
      store.commit("ticketDetailModule/SET_TpcIds", []);
      store.commit("ticketDetailModule/SET_ApprovalIds", []);

      store.commit("ticketDetailModule/SET_SELECTEDTICKETID", ticketId);
      await nextTick();

      markTicketReadForClient(ticketId);
      await AddTicketCommentsToCommentEntity(ticketId);
    } else {
      markTicketReadForClient(ticketId);
    }
  };

  const ticketListVirtualSyncKey = computed(() => {
    const list = TicketListForUI.value;
    return `${list.length}|${list
      .map((t) => `${t.ticketId}:${t.recentCommentId ?? ""}`)
      .join(",")}`;
  });

  function refreshVirtualListGeometry() {
    const v = virtualListRef.value;
    if (!v?.refresh) return;
    v.refresh(-1);
  }

  /** Reveal the active ticket when the list first mounts with a pre-selected id. */
  function scrollActiveTicketIntoView() {
    const v = virtualListRef.value;
    if (!v) return;
    const selectedId = activeChat.value;
    const list = TicketListForUI.value;
    if (!selectedId || String(selectedId).length === 0 || !list?.length) return;
    const idx = list.findIndex(
      (t) => String(t.ticketId) === String(selectedId),
    );
    if (idx >= 0 && typeof v.scrollTo === "function") {
      v.scrollTo(idx, "center");
    }
  }

  const debouncedRefreshVirtualList = debounce(refreshVirtualListGeometry, 32);

  const openChat = (id: string) => {
    setActiveChatForMobile?.(id);
    void SetSelectedTicketId(id);
  };
  /** The "Waiting for approval" chip. It can only ask for "the approval", not name one:
   * `isApprovalPending` is a server-side boolean over every approval on the ticket, so which tab
   * to land on is settled by the timeline once the ticket's conversations are loaded. */
  const openApproval = (id: string) => {
    store.commit("ticketDetailModule/SET_RequestedApprovalTicketId", id);
    openChat(id);
  };
  const navigateToTicketCreate = () => {
    router.replace({ name: "TicketCreateComponent" });
  };

  function messageListStatusRows(ticket: ticketEntity) {
    const u = TicketHelper.resolveStatusForMessageList(
      ticket.ticketStatusId as number | null | undefined,
    );
    return u ? [u] : [];
  }

  /**
   * Cleanup: remove selected ticket from store when leaving the page/component.
   */
  const removeSelectedTicketIdFromStore = () => {
    store.commit("ticketDetailModule/SET_SELECTEDTICKETID", "");
  };

  function attachTicketListResizeObserver() {
    const el = itemsViewportRef.value;
    if (!el || typeof ResizeObserver === "undefined") {
      return;
    }
    ticketListResizeObserver?.disconnect();
    ticketListResizeObserver = new ResizeObserver(() =>
      debouncedRefreshVirtualList(),
    );
    ticketListResizeObserver.observe(el);
  }

  function scheduleInitialVirtualListSync() {
    requestAnimationFrame(() => {
      if (!ticketListScopeActive) return;
      requestAnimationFrame(() => {
        if (!ticketListScopeActive) return;
        refreshVirtualListGeometry();
        scrollActiveTicketIntoView();
      });
    });
  }

  function scheduleVirtualListGeometryRefresh() {
    requestAnimationFrame(() => {
      if (!ticketListScopeActive) return;
      requestAnimationFrame(() => {
        if (!ticketListScopeActive) return;
        debouncedRefreshVirtualList();
      });
    });
  }

  function getTicketListScopeKey(): string {
    return [
      store.getters["ContactModule/getIsStateInitialized"] ? "1" : "0",
      store.getters["ContactModule/getHasAllTicketsTag"] ? "1" : "0",
      String(store.getters["ContactModule/getContactId"] ?? ""),
      String(store.getters["ContactModule/getClientId"] ?? ""),
    ].join("|");
  }

  function canUseTicketListSessionCache(
    query: string,
    scopeKey: string,
  ): boolean {
    return (
      ticketListSessionCache.hasLoaded &&
      ticketListSessionCache.query === query &&
      ticketListSessionCache.scopeKey === scopeKey
    );
  }

  function hydrateTicketListFromSessionCache() {
    ticketListCurrentPage.value = ticketListSessionCache.currentPage;
    ticketListHasMore.value = ticketListSessionCache.hasMore;
    loadingMoreTickets.value = false;
    ticketListLoadMoreInFlight = false;
    store.commit("ticketDetailModule/SET_TicketFillingState", true);
    scheduleAttachTicketListScrollListener();
  }

  function updateTicketListSessionCache(
    query: string,
    scopeKey: string,
    page: number,
    hasMore: boolean,
  ) {
    ticketListSessionCache.hasLoaded = true;
    ticketListSessionCache.query = query;
    ticketListSessionCache.scopeKey = scopeKey;
    ticketListSessionCache.currentPage = page;
    ticketListSessionCache.hasMore = hasMore;
  }

  async function loadTicketListPage(reset: boolean) {
    if (!reset && (ticketListLoadMoreInFlight || !ticketListHasMore.value)) {
      return;
    }

    const generation = ++ticketListFetchGeneration;
    const page = reset ? 1 : ticketListCurrentPage.value + 1;
    const query = ticketListServerQuery.value;
    const scopeKey = getTicketListScopeKey();

    if (reset) {
      store.commit("ticketDetailModule/SET_TicketFillingState", false);
      ticketListHasMore.value = true;
      loadingMoreTickets.value = false;
      ticketListLoadMoreInFlight = false;
    } else {
      loadingMoreTickets.value = true;
      ticketListLoadMoreInFlight = true;
    }

    try {
      const { hasMore, succeeded } =
        await EntitySyncManager.fetchTicketListPage(page, {
          append: !reset,
          query,
        });
      if (generation !== ticketListFetchGeneration) {
        return;
      }
      ticketListCurrentPage.value = page;
      ticketListHasMore.value = hasMore;
      if (succeeded) {
        updateTicketListSessionCache(query, scopeKey, page, hasMore);
      }
      bumpTicketSidebarListRevision();
      scheduleAttachTicketListScrollListener();
      scheduleFillTicketListViewport();
    } catch (e) {
      console.error(e);
    } finally {
      if (reset) {
        if (generation === ticketListFetchGeneration) {
          store.commit("ticketDetailModule/SET_TicketFillingState", true);
        }
        return;
      }
      loadingMoreTickets.value = false;
      ticketListLoadMoreInFlight = false;
    }
  }

  function resetAndLoadTicketList() {
    const query = ticketListServerQuery.value;
    const scopeKey = getTicketListScopeKey();
    if (!canUseTicketListSessionCache(query, scopeKey)) {
      ticketListSessionCache.hasLoaded = false;
    }
    ticketListCurrentPage.value = 0;
    ticketListHasMore.value = true;
    loadingMoreTickets.value = false;
    ticketListLoadMoreInFlight = false;
    void loadTicketListPage(true);
  }

  function isTicketListScrollable(): boolean {
    const el = ticketListScrollEl ?? getTicketListScrollElement();
    if (!el) {
      return false;
    }
    return el.scrollHeight > el.clientHeight + 1;
  }

  /** Quasar `virtualScroll` payload (`index` is position in full list, not slice `to`). */
  type VirtualScrollDetail = {
    index: number;
    from: number;
    to: number;
    direction?: string;
  };

  /**
   * The sidebar hides tickets with no visible message, so a full server page can leave the list
   * short - or empty - with no scrollbar. Scroll events are the only other trigger for the next
   * page, and a list that cannot scroll never fires one. Keep paging until the list fills the
   * view or the server has nothing more.
   */
  function scheduleFillTicketListViewport() {
    void nextTick(() => {
      requestAnimationFrame(() => {
        if (
          !ticketListScopeActive ||
          !ticketListHasMore.value ||
          ticketListLoadMoreInFlight
        ) {
          return;
        }
        if (isTicketListScrollable()) {
          return;
        }
        void loadTicketListPage(false);
      });
    });
  }

  function shouldLoadMoreTickets(nearEndIndex: number): boolean {
    const listLen = TicketListForUI.value.length;
    if (
      listLen === 0 ||
      !ticketListHasMore.value ||
      ticketListLoadMoreInFlight
    ) {
      return false;
    }

    const searchActive = searchTicketQuery.value.trim() !== "";
    // Client-side search can leave very few visible rows while more server pages exist.
    // Only auto-load when the user can scroll near the end (avoids spinner under 1–2 matches).
    if (searchActive && listLen < TICKET_LIST_LOAD_MORE_THRESHOLD) {
      return (
        isTicketListScrollable() && nearEndIndex >= Math.max(0, listLen - 1)
      );
    }

    return nearEndIndex >= listLen - TICKET_LIST_LOAD_MORE_THRESHOLD;
  }

  function maybeLoadMoreTickets(nearEndIndex: number) {
    if (!shouldLoadMoreTickets(nearEndIndex)) {
      return;
    }
    void loadTicketListPage(false);
  }

  function onTicketListVirtualScroll(detail: VirtualScrollDetail) {
    if (detail.direction === "decrease") {
      return;
    }
    maybeLoadMoreTickets(detail.index);
  }

  const debouncedTicketListScrollNearEnd = debounce(() => {
    const el = ticketListScrollEl;
    if (!el || !ticketListScopeActive) {
      return;
    }
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining > TICKET_LIST_SCROLL_END_PX) {
      return;
    }
    const listLen = TicketListForUI.value.length;
    maybeLoadMoreTickets(Math.max(0, listLen - 1));
  }, 80);

  function getTicketListScrollElement(): HTMLElement | null {
    const v = virtualListRef.value as { $el?: HTMLElement } | null;
    const el = v?.$el;
    return el instanceof HTMLElement ? el : null;
  }

  function detachTicketListScrollListener() {
    if (ticketListScrollEl) {
      ticketListScrollEl.removeEventListener(
        "scroll",
        debouncedTicketListScrollNearEnd,
      );
      ticketListScrollEl = null;
    }
    debouncedTicketListScrollNearEnd.cancel();
  }

  function attachTicketListScrollListener() {
    detachTicketListScrollListener();
    const el = getTicketListScrollElement();
    if (!el) {
      return;
    }
    ticketListScrollEl = el;
    el.addEventListener("scroll", debouncedTicketListScrollNearEnd, {
      passive: true,
    });
  }

  function scheduleAttachTicketListScrollListener() {
    void nextTick(() => {
      if (!ticketListScopeActive) {
        return;
      }
      requestAnimationFrame(() => {
        if (!ticketListScopeActive) {
          return;
        }
        attachTicketListScrollListener();
      });
    });
  }

  function scheduleTicketListSearchFetch() {
    if (ticketListSearchDebounce != null) {
      clearTimeout(ticketListSearchDebounce);
    }
    ticketListSearchDebounce = setTimeout(() => {
      ticketListSearchDebounce = undefined;
      const nextQuery = searchTicketQuery.value.trim();
      if (nextQuery === ticketListServerQuery.value) {
        return;
      }
      ticketListServerQuery.value = nextQuery;
      ticketListFetchGeneration += 1;
      resetAndLoadTicketList();
    }, TICKET_LIST_SEARCH_DEBOUNCE_MS);
  }

  onMounted(() => {
    ticketListScopeActive = true;
    sidebarTimeTick.value = Date.now();
    startSidebarTimeTick();
    attachTicketListResizeObserver();
    scheduleInitialVirtualListSync();
    scheduleAttachTicketListScrollListener();
    ticketListServerQuery.value = searchTicketQuery.value.trim();
    lastTicketListScopeKey = getTicketListScopeKey();
    if (
      canUseTicketListSessionCache(
        ticketListServerQuery.value,
        lastTicketListScopeKey,
      )
    ) {
      hydrateTicketListFromSessionCache();
    } else {
      resetAndLoadTicketList();
    }
  });

  /** Re-fetch when portal ticket scope changes (e.g. `hasAllTicketsTag` revoked) without full refresh. */
  watch(
    () =>
      [
        store.getters["ContactModule/getIsStateInitialized"] as boolean,
        store.getters["ContactModule/getHasAllTicketsTag"] as boolean,
        store.getters["ContactModule/getContactId"] as number,
        store.getters["ContactModule/getClientId"] as number | null,
      ] as const,
    ([initialized]) => {
      if (!initialized) {
        lastTicketListScopeKey = getTicketListScopeKey();
        return;
      }
      const nextScopeKey = getTicketListScopeKey();
      if (nextScopeKey === lastTicketListScopeKey) {
        return;
      }
      lastTicketListScopeKey = nextScopeKey;
      ticketListServerQuery.value = searchTicketQuery.value.trim();
      if (
        canUseTicketListSessionCache(ticketListServerQuery.value, nextScopeKey)
      ) {
        hydrateTicketListFromSessionCache();
        return;
      }
      resetAndLoadTicketList();
    },
    { flush: "post" },
  );

  watch(searchTicketQuery, () => {
    scheduleTicketListSearchFetch();
  });

  watch(ticketListVirtualSyncKey, () => debouncedRefreshVirtualList(), {
    flush: "post",
  });

  watch(TicketListForUI, () => scheduleAttachTicketListScrollListener(), {
    flush: "post",
  });

  onDeactivated(() => {
    ticketListScopeActive = false;
    ticketListFetchGeneration += 1;
    detachTicketListScrollListener();
    if (ticketListSearchDebounce != null) {
      clearTimeout(ticketListSearchDebounce);
      ticketListSearchDebounce = undefined;
    }
    stopSidebarTimeTick();
    ticketListResizeObserver?.disconnect();
    ticketListResizeObserver = undefined;
    debouncedRefreshVirtualList.cancel();
  });

  onActivated(() => {
    ticketListScopeActive = true;
    sidebarTimeTick.value = Date.now();
    startSidebarTimeTick();
    attachTicketListResizeObserver();
    scheduleVirtualListGeometryRefresh();
    scheduleAttachTicketListScrollListener();
  });

  onUnmounted(() => {
    ticketListScopeActive = false;
    ticketListFetchGeneration += 1;
    detachTicketListScrollListener();
    if (ticketListSearchDebounce != null) {
      clearTimeout(ticketListSearchDebounce);
      ticketListSearchDebounce = undefined;
    }
    stopSidebarTimeTick();
    ticketListResizeObserver?.disconnect();
    ticketListResizeObserver = undefined;
    debouncedRefreshVirtualList.cancel();
    removeSelectedTicketIdFromStore();
  });

  return {
    search,
    ticketListForUI: TicketListForUI,
    isTicketListEmpty,
    loadingTicketList,
    activeChat,
    openChat,
    openApproval,
    navigateToTicketCreate,
    itemsViewportRef,
    virtualListRef,
    messageListStatusRows,
    formatTicketSidebarTime,
    formatTicketSidebarTimeHover,
    ticketSidebarDisplayTimeIso,
    isDark,
    loadingMoreTickets,
    onTicketListVirtualScroll,
  };
}
